import React, { useState, useRef, useCallback } from "react";
import { toPng } from "html-to-image";
import toast from "react-hot-toast";

let nextId = 1;
const newLabel = () => ({
  id: nextId++,
  name: "4 Mukhi",
  size: "23.03 mm",
  x: 30,
  y: 30,
  color: "#E53935",
  fontSize: 22,
});

export default function EditPhoto() {
  const [image, setImage]       = useState(null);   // base64 data URL
  const [labels, setLabels]     = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [capturing, setCapturing]   = useState(false);

  const containerRef = useRef(null);
  const dragRef      = useRef(null);  // { id, offsetX, offsetY }
  const rafRef       = useRef(null);  // requestAnimationFrame handle

  // Convert file → base64 so html-to-image can embed it without fetch
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target.result);
      setLabels([]);
      setSelectedId(null);
    };
    reader.readAsDataURL(file);
  };

  const addLabel = () => {
    const l = newLabel();
    setLabels((p) => [...p, l]);
    setSelectedId(l.id);
  };

  const removeLabel = (id) => {
    setLabels((p) => p.filter((l) => l.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const update = (id, field, value) =>
    setLabels((p) => p.map((l) => (l.id === id ? { ...l, [field]: value } : l)));

  // ── Drag ──────────────────────────────────────────────────────────────────
  const startDrag = useCallback((clientX, clientY, id) => {
    setSelectedId(id);
    const rect = containerRef.current.getBoundingClientRect();
    setLabels((prev) => {
      const label = prev.find((l) => l.id === id);
      dragRef.current = {
        id,
        offsetX: clientX - (label.x / 100) * rect.width,
        offsetY: clientY - (label.y / 100) * rect.height,
      };
      return prev;
    });
  }, []);
//////
  const moveDrag = useCallback((clientX, clientY) => {
    if (!dragRef.current) return;
    // Throttle via RAF — prevents hundreds of setState/s that blank the screen
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (!dragRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.min(100, Math.max(0, ((clientX - dragRef.current.offsetX) / rect.width)  * 100));
      const y = Math.min(100, Math.max(0, ((clientY - dragRef.current.offsetY) / rect.height) * 100));
      setLabels((p) => p.map((l) => (l.id === dragRef.current?.id ? { ...l, x, y } : l)));
    });
  }, []);

  const stopDrag = useCallback(() => { dragRef.current = null; }, []);

  // ── Download ──────────────────────────────────────────────────────────────
  const download = async () => {
    if (!image) return toast.error("Please upload an image first");
    setCapturing(true);
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 150));
    try {
      const dataUrl = await toPng(containerRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        skipFonts: false,
      });
      const a = document.createElement("a");
      a.download = `makalu-photo-${Date.now()}.png`;
      a.href = dataUrl;
      a.click();
      toast.success("Downloaded!");
    } catch (err) {
      toast.error("Download failed — try again");
      console.error(err);
    } finally {
      setCapturing(false);
    }
  };

  const selected = labels.find((l) => l.id === selectedId);

  return (
    <div className="min-h-screen" style={{ background: "#0f0f0f" }}>
      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#FFD700", fontFamily: "serif" }}>Edit Photo</h1>
            <p className="text-sm mt-1" style={{ color: "#8A6000" }}>Upload · Add labels · Drag to position · Download</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <label className="btn-secondary text-sm px-4 py-2 cursor-pointer">
              Upload Photo
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            </label>
            {image && <>
              <button onClick={addLabel} className="btn-primary text-sm px-4 py-2">+ Add Label</button>
              <button onClick={download} disabled={capturing}
                className="text-sm px-4 py-2 rounded-lg font-semibold"
                style={{ background: capturing ? "#1a3a1a" : "linear-gradient(135deg,#1a6e1a,#28a428)", color: "#fff", border: "none", cursor: capturing ? "not-allowed" : "pointer" }}>
                {capturing ? "Saving…" : "↓ Download PNG"}
              </button>
            </>}
          </div>
        </div>

        <div className="flex gap-5" style={{ alignItems: "flex-start" }}>

          {/* ── Canvas area ─────────────────────────────────────────────── */}
          <div className="flex-1 rounded-2xl overflow-hidden"
            style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.2)", minHeight: 480 }}>

            {!image ? (
              <label className="flex flex-col items-center justify-center cursor-pointer"
                style={{ minHeight: 480 }}>
                <svg className="w-16 h-16 mb-4" style={{ color: "#3a2a00" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p style={{ color: "#5a4a30", fontSize: 16, fontWeight: 600 }}>Click to upload a photo</p>
                <p style={{ color: "#3a2a00", fontSize: 12, marginTop: 4 }}>JPG, PNG, WEBP</p>
                <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              </label>
            ) : (
              <div
                ref={containerRef}
                style={{ position: "relative", display: "block", lineHeight: 0, userSelect: "none" }}
                onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
                onTouchMove={(e) => { e.preventDefault(); moveDrag(e.touches[0].clientX, e.touches[0].clientY); }}
                onTouchEnd={stopDrag}
                onClick={() => setSelectedId(null)}
              >
                <img src={image} alt="Rudraksha" draggable={false}
                  style={{ width: "100%", display: "block", maxHeight: "75vh", objectFit: "contain" }} />

                {labels.map((label) => {
                  const sel = !capturing && label.id === selectedId;
                  return (
                    <div key={label.id}
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); startDrag(e.clientX, e.clientY, label.id); }}
                      onTouchStart={(e) => { e.stopPropagation(); startDrag(e.touches[0].clientX, e.touches[0].clientY, label.id); }}
                      onClick={(e) => { e.stopPropagation(); setSelectedId(label.id); }}
                      style={{
                        position: "absolute",
                        left: `${label.x}%`,
                        top: `${label.y}%`,
                        transform: "translate(-50%, -50%)",
                        cursor: "grab",
                        textAlign: "center",
                        lineHeight: 1.25,
                        padding: "3px 6px",
                        borderRadius: 4,
                        border: sel ? "1.5px dashed rgba(255,215,0,0.9)" : "1.5px dashed transparent",
                        background: sel ? "rgba(0,0,0,0.12)" : "transparent",
                        touchAction: "none",
                      }}>
                      <div style={{
                        color: label.color,
                        fontSize: label.fontSize,
                        fontWeight: 900,
                        fontFamily: "Arial, sans-serif",
                        whiteSpace: "nowrap",
                        textShadow: "1px 1px 4px rgba(0,0,0,0.8), -1px -1px 4px rgba(0,0,0,0.8)",
                      }}>
                        {label.name}
                      </div>
                      {label.size && (
                        <div style={{
                          color: label.color,
                          fontSize: label.fontSize * 0.78,
                          fontWeight: 700,
                          fontFamily: "Arial, sans-serif",
                          whiteSpace: "nowrap",
                          textShadow: "1px 1px 4px rgba(0,0,0,0.8), -1px -1px 4px rgba(0,0,0,0.8)",
                        }}>
                          ({label.size})
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Controls panel ──────────────────────────────────────────── */}
          {image && (
            <div style={{ width: 272, flexShrink: 0 }}>

              {selected ? (
                <div className="rounded-2xl p-5 mb-4"
                  style={{ background: "#1a1a1a", border: "1px solid rgba(200,150,12,0.35)" }}>
                  <div style={{ height: 2, background: "linear-gradient(90deg,transparent,#C8960C,transparent)", marginBottom: 16, borderRadius: 1 }} />
                  <h3 style={{ color: "#FFD700", fontFamily: "serif", fontSize: 13, fontWeight: 700, marginBottom: 14 }}>Edit Label</h3>

                  <div className="space-y-3">
                    <div>
                      <label style={{ display:"block", fontSize:10, color:"#8A6000", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:5 }}>Name</label>
                      <input className="input-field w-full" value={selected.name}
                        onChange={(e) => update(selected.id, "name", e.target.value)}
                        placeholder="e.g. 4 Mukhi" />
                    </div>
                    <div>
                      <label style={{ display:"block", fontSize:10, color:"#8A6000", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:5 }}>Size</label>
                      <input className="input-field w-full" value={selected.size}
                        onChange={(e) => update(selected.id, "size", e.target.value)}
                        placeholder="e.g. 23.03 mm" />
                    </div>
                    <div className="flex gap-3">
                      <div style={{ flex:1 }}>
                        <label style={{ display:"block", fontSize:10, color:"#8A6000", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:5 }}>Color</label>
                        <input type="color" value={selected.color}
                          onChange={(e) => update(selected.id, "color", e.target.value)}
                          style={{ width:"100%", height:38, borderRadius:8, border:"1px solid rgba(200,150,12,0.3)", background:"transparent", cursor:"pointer", padding:2 }} />
                      </div>
                      <div style={{ flex:1 }}>
                        <label style={{ display:"block", fontSize:10, color:"#8A6000", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:5 }}>Font px</label>
                        <input type="number" className="input-field w-full"
                          value={selected.fontSize} min={10} max={80}
                          onChange={(e) => update(selected.id, "fontSize", Number(e.target.value))} />
                      </div>
                    </div>
                    <button onClick={() => removeLabel(selected.id)}
                      className="w-full py-2 rounded-lg text-sm font-medium"
                      style={{ background:"rgba(127,29,29,0.2)", color:"#fca5a5", border:"1px solid rgba(127,29,29,0.4)", cursor:"pointer" }}>
                      Delete Label
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl p-4 mb-4"
                  style={{ background:"#1a1a1a", border:"1px solid rgba(200,150,12,0.12)" }}>
                  <p style={{ fontSize:12, color:"#5a4a30", textAlign:"center" }}>
                    {labels.length === 0 ? "Click \"+ Add Label\" to start" : "Click a label on the image to edit it"}
                  </p>
                </div>
              )}

              {labels.length > 0 && (
                <div className="rounded-2xl p-4"
                  style={{ background:"#1a1a1a", border:"1px solid rgba(200,150,12,0.15)" }}>
                  <p style={{ fontSize:10, color:"#8A6000", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:10 }}>All Labels</p>
                  <div className="space-y-2">
                    {labels.map((l) => (
                      <div key={l.id} onClick={() => setSelectedId(l.id)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
                        style={{ background: l.id === selectedId ? "rgba(200,150,12,0.1)" : "rgba(255,255,255,0.03)", border:`1px solid ${l.id === selectedId ? "rgba(200,150,12,0.4)" : "transparent"}` }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:l.color, flexShrink:0 }} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <p style={{ fontSize:12, color:"#f5f0e8", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.name}</p>
                          {l.size && <p style={{ fontSize:10, color:"#8A6000" }}>({l.size})</p>}
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); removeLabel(l.id); }}
                          style={{ color:"#7f1d1d", background:"none", border:"none", cursor:"pointer", fontSize:14, padding:"0 2px" }}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p style={{ fontSize:10, color:"#3a2a00", textAlign:"center", marginTop:14, lineHeight:1.7 }}>
                Drag labels to position them<br />Download saves at 3× quality
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
